import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogIn, Eye, EyeOff } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[480px] bg-gradient-to-b from-slate-950 to-slate-900 flex-col justify-between p-12">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            deal <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">desk</span>
          </h1>
          <p className="text-slate-400 text-sm font-medium mt-1">manage your deals</p>
        </div>

        <div className="space-y-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Why teams love deal desk</p>
            <div className="space-y-4">
              {[
                'Track every deal from first touch to close',
                'Customizable pipeline stages for your workflow',
                'Instant exports and bulk uploads',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                  <p className="text-slate-300 text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-600">Built for modern sales teams</p>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="lg:hidden mb-6">
              <h2 className="text-2xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                deal <span className="text-indigo-600">desk</span>
              </h2>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome Back
            </h1>
            <p className="text-lg text-slate-500">sign in to your account</p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="mt-1.5 text-right">
                  <Link to="/forgot-password" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              </div>

              {error && (
                <div data-testid="login-error-message" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg py-3 shadow-sm hover:shadow-md transition-all font-semibold text-base"
                disabled={loading}
              >
                <LogIn size={18} strokeWidth={2} className="mr-2" />
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Don't have an account?{' '}
                <Link to="/register" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
